import { Card } from "@/components/ui/card";
import { Info, Printer } from "lucide-react";
import { AccionesMenu } from "@/components/shell/AccionesMenu";
import { TarjetaPeriodo, TarjetaTotal } from "@/components/shell/TarjetasPeriodo";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { fmtFecha, fmtMXN, r2 } from "@/lib/operativo";
import { useOperativo } from "@/lib/operativo-store";

export function TablaProgramada() {
  const { programada, condiciones, solicitud } = useOperativo();
  const tot = programada.reduce(
    (a, f) => ({
      capital: r2(a.capital + f.capital),
      interes: r2(a.interes + f.interes),
      iva: r2(a.iva + f.ivaInteres),
      base: r2(a.base + f.cuotaBase),
      cargos: r2(a.cargos + f.cargosPeriodo),
      cuota: r2(a.cuota + f.pagoTotal),
    }),
    { capital: 0, interes: 0, iva: 0, base: 0, cargos: 0, cuota: 0 },
  );

  const fechaRecorrida = (original: string, habil: string) => original !== habil;
  const motivoRecorrido = (original: string) => {
    const dia = new Date(`${original}T00:00:00Z`).getUTCDay();
    return dia === 0 || dia === 6
      ? "La fecha se recorrió al siguiente día hábil porque la fecha original cayó en fin de semana."
      : "La fecha se recorrió al siguiente día hábil porque la fecha original cayó en un día festivo.";
  };

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-navy sm:text-2xl">
            Calendario de pagos programado
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Periodicidad {solicitud.params.tipoPago.toLowerCase()} · {solicitud.params.amortizacion}{" "}
            · {solicitud.params.calculoInteres} · {condiciones.periodosGracia} período(s) de gracia
            de solo interés. Las fechas que caen en fin de semana o día inhábil se recorren al
            siguiente día hábil.
          </p>
        </div>

        <Card className="print-block overflow-hidden border-navy/15 p-0 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-surface px-5 py-4">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-navy">
              Tabla de amortización programada
            </h3>
            <AccionesMenu
              acciones={[
                {
                  label: "Imprimir / PDF",
                  icono: <Printer className="h-4 w-4" />,
                  variant: "secondary",
                  onClick: () => window.print(),
                },
              ]}
            />
          </div>
          <div className="solo-movil space-y-3 p-4 lg:hidden">
            {programada.map((f) => (
              <TarjetaPeriodo
                key={f.periodo}
                titulo={
                  <>
                    Período {f.periodo}
                    {f.esGracia && (
                      <Badge variant="secondary" className="text-[10px]">
                        gracia
                      </Badge>
                    )}
                  </>
                }
                fecha={
                  <span className="inline-flex flex-wrap items-center gap-1">
                    {fmtFecha(f.fechaProg)}
                    {fechaRecorrida(f.fechaOriginal, f.fechaProg) && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge variant="outline" className="text-[10px]">
                            recorrido
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          {motivoRecorrido(f.fechaOriginal)}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </span>
                }
                destacado={{ l: "Pago total", v: fmtMXN(f.pagoTotal) }}
                resumen={[
                  { l: "Fecha original", v: fmtFecha(f.fechaOriginal) },
                  { l: "Días del período", v: f.diasPeriodo },
                  { l: "Saldo inicial", v: fmtMXN(f.saldoInicial) },
                  { l: "Saldo final", v: fmtMXN(f.saldoFinal) },
                ]}
                detalle={[
                  { l: "Cuota base", v: fmtMXN(f.cuotaBase) },
                  { l: "Capital", v: fmtMXN(f.capital), tono: "emerald" },
                  { l: "Interés", v: fmtMXN(f.interes), tono: "chart3" },
                  { l: "IVA del interés", v: fmtMXN(f.ivaInteres), tono: "muted" },
                  { l: "Comisiones / gastos", v: fmtMXN(f.cargosPeriodo), tono: "muted" },
                  { l: "Pago total", v: fmtMXN(f.pagoTotal) },
                ]}
              />
            ))}
            <TarjetaTotal
              datos={[
                { l: "Total cuota base", v: fmtMXN(tot.base) },
                { l: "Total capital", v: fmtMXN(tot.capital), tono: "emerald" },
                { l: "Total interés", v: fmtMXN(tot.interes), tono: "chart3" },
                { l: "Total IVA del interés", v: fmtMXN(tot.iva), tono: "muted" },
                { l: "Total comisiones / gastos", v: fmtMXN(tot.cargos), tono: "muted" },
                { l: "Total a pagar", v: fmtMXN(tot.cuota) },
              ]}
            />
          </div>

          <div className="solo-desktop hidden overflow-x-auto lg:block">
            <table className="w-full min-w-[1240px] text-sm">
              <thead>
                <tr className="bg-navy text-primary-foreground">
                  <th className="px-3 py-2.5 text-left font-medium">No. de pago</th>
                  <th className="px-3 py-2.5 text-left font-medium">Fecha original</th>
                  <th className="px-3 py-2.5 text-left font-medium">Día hábil de pago</th>
                  <th className="px-3 py-2.5 text-right font-medium">
                    <span className="inline-flex items-center justify-end gap-1">
                      Días del período
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info
                            className="h-3.5 w-3.5 cursor-help"
                            aria-label="Información sobre días del período"
                          />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-sm">
                          La tabla programada calcula el interés con la tasa del período; el devengo
                          real se calcula por días corridos (base 360).
                        </TooltipContent>
                      </Tooltip>
                    </span>
                  </th>
                  <th className="px-3 py-2.5 text-right font-medium">Saldo inicial</th>
                  <th className="px-3 py-2.5 text-right font-medium">Cuota base</th>
                  <th className="px-3 py-2.5 text-right font-medium">Capital</th>
                  <th className="px-3 py-2.5 text-right font-medium">Interés</th>
                  <th className="px-3 py-2.5 text-right font-medium">IVA del interés</th>
                  <th className="px-3 py-2.5 text-right font-medium">Comisiones / gastos</th>
                  <th className="px-3 py-2.5 text-right font-medium">Pago total</th>
                  <th className="px-3 py-2.5 text-right font-medium">Saldo final</th>
                </tr>
              </thead>
              <tbody>
                {programada.map((f) => (
                  <tr key={f.periodo} className="border-b last:border-0 odd:bg-surface/60">
                    <td className="px-3 py-2 num">
                      {f.periodo}
                      {f.esGracia && (
                        <Badge variant="secondary" className="ml-1.5 text-[10px]">
                          gracia
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 num text-muted-foreground">
                      {fmtFecha(f.fechaOriginal)}
                    </td>
                    <td className="px-3 py-2 num text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        {fmtFecha(f.fechaProg)}
                        {fechaRecorrida(f.fechaOriginal, f.fechaProg) && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="outline" className="text-[10px]">
                                recorrido
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              {motivoRecorrido(f.fechaOriginal)}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right num">{f.diasPeriodo}</td>
                    <td className="px-3 py-2 text-right num">{fmtMXN(f.saldoInicial)}</td>
                    <td className="px-3 py-2 text-right num">{fmtMXN(f.cuotaBase)}</td>
                    <td className="px-3 py-2 text-right num text-emerald">{fmtMXN(f.capital)}</td>
                    <td className="px-3 py-2 text-right num text-chart-3">{fmtMXN(f.interes)}</td>
                    <td className="px-3 py-2 text-right num text-muted-foreground">
                      {fmtMXN(f.ivaInteres)}
                    </td>
                    <td className="px-3 py-2 text-right num text-muted-foreground">
                      {fmtMXN(f.cargosPeriodo)}
                    </td>
                    <td className="px-3 py-2 text-right num font-semibold">
                      {fmtMXN(f.pagoTotal)}
                    </td>
                    <td className="px-3 py-2 text-right num">{fmtMXN(f.saldoFinal)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-navy-soft font-semibold text-navy">
                  <td className="px-3 py-2.5" colSpan={5}>
                    TOTAL
                  </td>
                  <td className="px-3 py-2.5 text-right num">{fmtMXN(tot.base)}</td>
                  <td className="px-3 py-2.5 text-right num">{fmtMXN(tot.capital)}</td>
                  <td className="px-3 py-2.5 text-right num">{fmtMXN(tot.interes)}</td>
                  <td className="px-3 py-2.5 text-right num">{fmtMXN(tot.iva)}</td>
                  <td className="px-3 py-2.5 text-right num">{fmtMXN(tot.cargos)}</td>
                  <td className="px-3 py-2.5 text-right num">{fmtMXN(tot.cuota)}</td>
                  <td className="px-3 py-2.5 text-right num">{fmtMXN(0)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      </div>
    </TooltipProvider>
  );
}
